<template>
  <div class="GoodsList wide-container">
    <!-- 1. 多维属性与标段筛选面板卡片 -->
    <div class="filter-panel-card">
      <div class="filter-card-header">
        <div class="header-left">
          <span class="accent-bar"></span>
          <span class="header-title">物料多维检索与框架筛选</span>
          <span class="header-desc" v-if="keyword">关键词：“{{ keyword }}”</span>
        </div>
        <div class="header-right">
          <span class="result-stat" v-if="!goodsLoading">
            共找到 <strong class="total-num">{{ total }}</strong> 项符合协议的入库商品
          </span>
          <span class="result-stat" v-else>
            <i class="el-icon-loading"></i> 正在检索入库商品...
          </span>
          <button
            v-if="segmentId || brandId"
            class="reset-btn"
            type="button"
            @click="resetFilters"
          >
            <i class="el-icon-refresh-left"></i> 重置筛选
          </button>
        </div>
      </div>

      <div class="filter-card-body">
        <!-- 标段筛选行 -->
        <div class="filter-row" v-if="segmentList && segmentList.length">
          <div class="filter-label">标段划分</div>
          <div class="filter-values segment-values">
            <button
              type="button"
              class="filter-pill-btn"
              :class="{ active: !segmentId }"
              @click="changeSegment('')"
            >
              全部标段
            </button>
            <button
              v-for="segment in segmentList"
              :key="segment.id"
              type="button"
              class="filter-pill-btn"
              :class="{ active: segmentId === segment.id }"
              @click="changeSegment(segment.id)"
            >
              {{ segment.name }}
            </button>
          </div>
        </div>

        <!-- 品牌筛选行 (A-Z 拼音索引 + 品牌胶囊) -->
        <div class="filter-row brand-row" v-if="brandList && brandList.length">
          <div class="filter-label">品牌筛选</div>
          <div class="filter-values brand-content">
            <!-- A-Z 首字母横向检索条 -->
            <div class="brand-index-bar">
              <button
                type="button"
                class="initial-btn"
                :class="{ active: !brandId && !activeInitial }"
                @click="resetBrandInitial"
              >
                全部
              </button>
              <button
                v-for="initial in brandIndexes"
                :key="initial"
                type="button"
                class="initial-btn"
                :disabled="!availableBrandIndexes[initial]"
                :class="{ active: activeInitial === initial }"
                @click="selectBrandInitial(initial)"
              >
                {{ initial }}
              </button>
            </div>

            <!-- 当前字母下的品牌列表 -->
            <div class="brand-pills-wrap" v-if="currentBrandGroups && currentBrandGroups.length">
              <div
                v-for="group in currentBrandGroups"
                :key="group.initial"
                class="brand-group-line"
              >
                <span class="group-letter">{{ group.initial }}</span>
                <div class="group-pills">
                  <button
                    v-for="brand in group.brands"
                    :key="brand.id"
                    type="button"
                    class="brand-pill-btn"
                    :class="{ active: brandId === brand.id }"
                    @click="changeBrand(brand.id)"
                    :title="brand.name"
                  >
                    {{ brand.name }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 当前激活筛选标签摘要 -->
        <div class="filter-summary-row" v-if="segmentId || brandId">
          <span class="summary-label">已选条件：</span>
          <div class="summary-tags">
            <span class="summary-tag" v-if="segmentId">
              标段：{{ currentSegmentName }}
              <i class="el-icon-close close-icon" @click="changeSegment('')"></i>
            </span>
            <span class="summary-tag" v-if="brandId">
              品牌：{{ currentBrandName }}
              <i class="el-icon-close close-icon" @click="changeBrand('')"></i>
            </span>
            <button class="clear-all-link" type="button" @click="resetFilters">
              清空所有条件
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. 商品列表工具条与展示区域 -->
    <div class="goods-list-section">
      <!-- 列表顶部工具栏 -->
      <div class="list-toolbar">
        <div class="toolbar-left">
          <span class="toolbar-title">商品列表</span>
          <span class="toolbar-sub">（第 {{ page }} 页 / 每页 {{ limit }} 项）</span>
        </div>
        <div class="toolbar-right">
          <span class="contract-badge">
            <i class="el-icon-circle-check"></i> 框架协议入库品 · 实时开票
          </span>
        </div>
      </div>

      <!-- 态 1：骨架屏加载态 (流光微动卡片网格，5列等宽，高度完全一致，消除空数据闪现) -->
      <div class="goods-skeleton-grid" v-if="goodsLoading">
        <div class="goods-skeleton-card" v-for="n in 10" :key="'sk-' + n">
          <div class="skeleton-shimmer"></div>
          <div class="sk-img-box"></div>
          <div class="sk-info-box">
            <div class="sk-line title"></div>
            <div class="sk-line sku"></div>
            <div class="sk-bottom">
              <div class="sk-line price"></div>
              <div class="sk-line tag"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 态 2：真实商品卡片 5 列网格 (沿用已重构完毕的 GoodsItem 组件) -->
      <template v-else-if="goodsList && goodsList.length > 0">
        <ul class="goods-grid fade-in">
          <GoodsItem
            v-for="(item, index) in goodsList"
            :key="item.id || index"
            :id="item.id"
            :img="
              item.rguiigpafo0pe0my && item.rguiigpafo0pe0my.length > 0
                ? item.rguiigpafo0pe0my[0].url
                : ''
            "
            :name="item.avdrlfwlhg7kywxn"
            :price="item.f93blbqlvdtdt65v"
            :section="item.dzornqdwofgytboq"
            :supplier-name="
              item.cijhopahvvolyisa && item.cijhopahvvolyisa.length > 0
                ? item.cijhopahvvolyisa[0].value
                : ''
            "
            :sku="item.levmoblohezp7y50"
          />
        </ul>

        <!-- 分页控件 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="page"
            :page-sizes="[10, 20, 40, 80]"
            :page-size="limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            background
          >
          </el-pagination>
        </div>
      </template>

      <!-- 态 3：优雅空状态卡片 (仅当非加载态且确认无数据时才展示) -->
      <div class="empty-goods-card fade-in" v-else>
        <div class="empty-icon-box">
          <i class="el-icon-search"></i>
        </div>
        <h3 class="empty-title">未找到符合当前筛选条件的物资</h3>
        <p class="empty-hint">
          请尝试调整或重置筛选条件，或者在顶栏搜索框中输入物料名称、型号或原厂 SKU 订货号重新查询。
        </p>
        <button class="empty-reset-btn" type="button" @click="resetFilters">
          <i class="el-icon-refresh-left"></i> 重置所有筛选条件
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GoodsItem from "../../components/GoodsItem";
import { online } from "@/api/platform";
const { getGoodsCategoryId } = require("@/util/goodsCategoryQuery");
const {
  BRAND_INDEXES,
  normalizeBrands,
  groupBrands,
} = require("@/util/brandIndex");
const { normalizeSegments } = require("@/util/segmentFilter");

export default {
  name: "GoodsList",
  components: {
    GoodsItem,
  },
  computed: {
    isSearchPage () {
      return Number(this.typeId) === 0;
    },
    typeId () {
      return this.$route.params.typeId || 0;
    },
    keyword () {
      return this.$route.query.searchText || "";
    },
    brandGroups () {
      return groupBrands(this.brandList);
    },
    currentBrandGroups () {
      const initial = this.activeInitial || (
        this.brandGroups.length ? this.brandGroups[0].initial : ""
      );
      return this.brandGroups.filter((group) => group.initial === initial);
    },
    availableBrandIndexes () {
      return this.brandGroups.reduce((indexes, group) => {
        indexes[group.initial] = true;
        return indexes;
      }, {});
    },
    currentSegmentName () {
      if (!this.segmentId) return '';
      const found = this.segmentList.find(s => s.id === this.segmentId);
      return found ? found.name : this.segmentId;
    },
    currentBrandName () {
      if (!this.brandId) return '';
      const found = this.brandList.find(b => b.id === this.brandId);
      return found ? found.name : this.brandId;
    },
  },
  data () {
    return {
      goodsLoading: true,
      goodsList: [],
      brandId: "",
      segmentId: "",
      page: 1, // 页数
      limit: 20,
      total: 0,
      form_head_uuid: "1906dfc5e0ce4e7cb7d6324b06e6d470",
      brandList: [],
      segmentList: [],
      brandIndexes: BRAND_INDEXES,
      activeInitial: "",
    };
  },

  methods: {
    async getListSegments () {
      try {
        const { result } = await online({
          page: 1,
          limit: 10000,
          keyword: "",
          select_option: "",
          form_head_uuid: "08af2ad72e1344b189f33e01719490da",
        });
        this.segmentList = normalizeSegments(result);
      } catch (error) {
        this.segmentList = [];
      }
    },
    async getListBrands () {
      try {
        const { result } = await online({
          page: 1,
          limit: 10000,
          keyword: "",
          select_option: "",
          form_head_uuid: "bdc9ab24525d43818588fa136d5cd3da",
        });
        this.brandList = normalizeBrands(result);
      } catch (error) {
        this.brandList = [];
      }
      this.activeInitial = this.brandGroups.length
        ? this.brandGroups[0].initial
        : "";
    },
    async getGoodsList () {
      this.goodsLoading = true;
      const { page, form_head_uuid, brandId, segmentId, limit } = this;
      const categoryId = getGoodsCategoryId(this.$route.query);
      try {
        const { meta, result } = await online({
          page,
          limit,
          keyword: this.keyword,
          select_option: "",
          form_head_uuid,
          lkru2wjadtwwzlh8: brandId ? [brandId] : "",
          dzornqdwofgytboq: segmentId ? [segmentId] : "",
          '8ufiw69wm435kw5j': categoryId ? [categoryId] : '',
        });
        this.goodsList = result || [];
        this.total = meta ? meta.total : 0;
      } catch (error) {
        this.goodsList = [];
        this.total = 0;
      } finally {
        this.goodsLoading = false;
      }
    },
    changeBrand (e) {
      this.page = 1;
      this.brandId = this.brandId === e ? "" : e;
      this.getGoodsList();
    },
    changeSegment (segmentId) {
      this.page = 1;
      this.segmentId = this.segmentId === segmentId ? "" : segmentId;
      this.getGoodsList();
    },
    selectBrandInitial (initial) {
      if (!this.availableBrandIndexes[initial]) return;
      this.activeInitial = initial;
    },
    resetBrandInitial () {
      this.activeInitial = this.brandGroups.length ? this.brandGroups[0].initial : "";
      this.brandId = "";
      this.page = 1;
      this.getGoodsList();
    },
    resetFilters () {
      this.segmentId = "";
      this.brandId = "";
      this.page = 1;
      this.getGoodsList();
    },
    handleSizeChange (limit) {
      this.page = 1;
      this.limit = limit;
      this.getGoodsList();
    },
    handleCurrentChange (page) {
      this.page = page;
      this.getGoodsList();
    },
  },

  mounted () {
    this.getListSegments();
    this.getListBrands();
    if (!this.isSearchPage) {
      this.getGoodsList();
    } else {
      this.page = 1;
      this.getGoodsList();
    }
  },

  watch: {
    $route () {
      this.page = 1;
      this.getGoodsList();
    },
  },
};
</script>

<style scoped lang="less">
@import '../../assets/css/var.less';

.GoodsList {
  padding: 16px 0 60px;

  // 1. 顶部多维筛选卡片
  .filter-panel-card {
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 4px rgba(0, 40, 100, 0.04);
    margin-bottom: 20px;
    overflow: hidden;

    .filter-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 20px;
      border-bottom: 1px solid #edf2f7;
      background-color: #fafbfc;

      .header-left {
        display: flex;
        align-items: center;
        gap: 10px;

        .accent-bar {
          display: inline-block;
          width: 3px;
          height: 16px;
          background: linear-gradient(180deg, @brandPrimary 0%, @brandContainer 100%);
          border-radius: 2px;
        }

        .header-title {
          font-size: 14px;
          font-weight: 700;
          color: @brandTextDark;
        }

        .header-desc {
          font-size: 12px;
          color: @brandTextLight;
          background-color: #edf2f7;
          padding: 2px 8px;
          border-radius: 4px;
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 16px;

        .result-stat {
          font-size: 13px;
          color: @brandTextLight;

          .total-num {
            color: @brandPrimary;
            font-weight: 700;
            font-size: 14px;
          }
        }

        .reset-btn {
          border: 1px solid #cbd5e1;
          background-color: #ffffff;
          color: @brandTextDark;
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.15s ease;

          &:hover {
            color: @brandPrimary;
            border-color: @brandPrimary;
            background-color: #f0f7ff;
          }
        }
      }
    }

    .filter-card-body {
      padding: 12px 20px;

      .filter-row {
        display: flex;
        align-items: flex-start;
        padding: 10px 0;
        border-bottom: 1px dashed #f1f5f9;

        &:last-child {
          border-bottom: none;
        }

        .filter-label {
          flex: 0 0 80px;
          font-size: 13px;
          font-weight: 600;
          color: @brandTextLight;
          line-height: 28px;
        }

        .filter-values {
          flex: 1;
          min-width: 0;

          &.segment-values {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .filter-pill-btn {
            height: 28px;
            line-height: 26px;
            padding: 0 14px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            border: 1px solid #e2e8f0;
            background-color: #ffffff;
            color: @brandTextDark;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
              color: @brandPrimary;
              border-color: @brandContainer;
              background-color: #f8fafc;
            }

            &.active {
              background-color: @brandPrimary;
              border-color: @brandPrimary;
              color: #ffffff;
              font-weight: 600;
              box-shadow: 0 2px 4px rgba(0, 60, 144, 0.2);
            }
          }

          &.brand-content {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .brand-index-bar {
              display: flex;
              flex-wrap: wrap;
              gap: 4px;

              .initial-btn {
                min-width: 24px;
                height: 24px;
                line-height: 22px;
                padding: 0 4px;
                font-size: 12px;
                font-weight: 600;
                text-align: center;
                border-radius: 4px;
                border: 1px solid transparent;
                background-color: #f8fafc;
                color: @brandTextDark;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover:not(:disabled) {
                  color: @brandPrimary;
                  background-color: #e2e8f0;
                }

                &.active {
                  background-color: @brandPrimary;
                  color: #ffffff;
                  border-color: @brandPrimary;
                }

                &:disabled {
                  color: #cbd5e1;
                  cursor: not-allowed;
                  background-color: transparent;
                }
              }
            }

            .brand-pills-wrap {
              background-color: #f8fafc;
              border: 1px solid #edf2f7;
              border-radius: 6px;
              padding: 10px 14px;
              max-height: 110px;
              overflow-y: auto;
              scrollbar-width: thin;

              .brand-group-line {
                display: flex;
                align-items: center;
                gap: 12px;

                .group-letter {
                  font-size: 12px;
                  font-weight: 700;
                  color: @brandTextLight;
                  flex: 0 0 16px;
                }

                .group-pills {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 8px;

                  .brand-pill-btn {
                    height: 26px;
                    line-height: 24px;
                    padding: 0 10px;
                    font-size: 12px;
                    border-radius: 4px;
                    border: 1px solid #e2e8f0;
                    background-color: #ffffff;
                    color: @brandTextDark;
                    cursor: pointer;
                    transition: all 0.15s ease;

                    &:hover {
                      border-color: @brandPrimary;
                      color: @brandPrimary;
                    }

                    &.active {
                      background-color: @brandPrimary;
                      border-color: @brandPrimary;
                      color: #ffffff;
                      font-weight: 600;
                    }
                  }
                }
              }
            }
          }
        }
      }

      // 已选摘要
      .filter-summary-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding-top: 10px;
        font-size: 12px;

        .summary-label {
          color: @brandTextLight;
        }

        .summary-tags {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;

          .summary-tag {
            background-color: #eff6ff;
            border: 1px solid #bfdbfe;
            color: @brandPrimary;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
            gap: 4px;

            .close-icon {
              cursor: pointer;
              border-radius: 50%;
              padding: 1px;

              &:hover {
                background-color: #dbeafe;
                color: #ef4444;
              }
            }
          }

          .clear-all-link {
            border: 0;
            background: transparent;
            color: #ef4444;
            font-size: 12px;
            cursor: pointer;
            padding: 0;
            margin-left: 6px;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }

  // 2. 商品列表区块
  .goods-list-section {
    .list-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 14px;
      padding: 0 4px;

      .toolbar-left {
        display: flex;
        align-items: baseline;
        gap: 8px;

        .toolbar-title {
          font-size: 16px;
          font-weight: 700;
          color: @brandTextDark;
        }

        .toolbar-sub {
          font-size: 12px;
          color: @brandTextLight;
        }
      }

      .toolbar-right {
        .contract-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #10b981;
          font-weight: 500;
        }
      }
    }

    // 5 列等宽自适应卡片网格
    .goods-grid {
      display: grid;
      grid-gap: 12px;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      padding: 0;
      margin: 0;
      list-style: none;
      box-sizing: border-box;
    }

    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 32px;
      padding: 16px 0;

      /deep/ .el-pagination.is-background .el-pager li:not(.disabled).active {
        background-color: @brandPrimary;
        color: #ffffff;
      }
    }

    // 空状态卡片
    .empty-goods-card {
      background-color: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 60px 20px;
      text-align: center;
      margin: 20px 0;

      .empty-icon-box {
        width: 64px;
        height: 64px;
        background-color: #f1f5f9;
        color: #94a3b8;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        margin: 0 auto 16px;
      }

      .empty-title {
        font-size: 16px;
        font-weight: 700;
        color: @brandTextDark;
        margin: 0 0 8px;
      }

      .empty-hint {
        font-size: 13px;
        color: @brandTextLight;
        max-width: 480px;
        margin: 0 auto 20px;
        line-height: 1.6;
      }

      .empty-reset-btn {
        height: 36px;
        line-height: 36px;
        padding: 0 18px;
        background-color: @brandPrimary;
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: background 0.15s ease;

        &:hover {
          background-color: @brandContainer;
        }
      }
    }

    // 骨架屏网格 (5 列等宽自适应，高度对齐真实卡片，杜绝空数据闪现)
    .goods-skeleton-grid {
      display: grid;
      grid-gap: 12px;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      padding: 0;
      margin: 0 0 24px;
      box-sizing: border-box;

      .goods-skeleton-card {
        background-color: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 12px;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        gap: 12px;

        .skeleton-shimmer {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(241, 245, 249, 0.75) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          transform: translateX(-100%);
          animation: shimmerMove 1.5s infinite;
          pointer-events: none;
          z-index: 2;
        }

        .sk-img-box {
          width: 100%;
          height: 190px;
          background-color: #f1f5f9;
          border-radius: 8px;
        }

        .sk-info-box {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .sk-line {
            background-color: #f1f5f9;
            border-radius: 4px;

            &.title {
              height: 16px;
              width: 85%;
            }

            &.sku {
              height: 12px;
              width: 50%;
            }

            &.price {
              height: 20px;
              width: 45%;
            }

            &.tag {
              height: 14px;
              width: 35%;
            }
          }

          .sk-bottom {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 4px;
          }
        }
      }
    }
  }
}

.fade-in {
  animation: fadeIn 0.35s ease forwards;
}

@keyframes shimmerMove {
  100% {
    transform: translateX(100%);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/client/GoodsList.vue